import { DragMoveEvent, DragOverlay, useDndContext, useDndMonitor } from '@dnd-kit/core'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import moment from 'moment'
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'

import { usePSUser } from '../../hooks'
import { useScrollIntoView } from '../../hooks/useScrollIntoView'
import { useWindowResize } from '../../hooks/useWindowResize'
import {
    Bookable,
    BookablesByDate,
    getBookableCardSize,
    getBookableDuration,
    getBookableType,
    groupOverlappingSortedBookables,
    isMultiDay,
    sortBookablesByStartTime,
} from '../../models/Bookable'
import { Booking } from '../../models/Booking'
import { CalendarFilter } from '../../models/CalendarFilter'
import { Payee } from '../../models/Payee'
import { User } from '../../models/User'
import {
    ALL_DAY_HEADER_HEIGHT,
    BOOKABLE_GAP,
    BOOKABLE_PADDING_OFFSET,
    CALENDAR_PADDING,
    CalendarDragStartEvent,
    CalendarDragState,
    DAY_HEADER_HEIGHT,
    DAY_MIN_WIDTH,
    MIN_BOOKABLE_WIDTH,
    OVERFLOW_BUTTON_OFFSET,
    PIXELS_PER_MINUTE,
    TIME_COLUMN_OFFSET,
    getUserStartHours,
    initialDragState,
} from '../../utils/calendar'
import {
    DAILY_HOURS,
    DATE_KEY_FORMAT,
    SHORT_DOW_FORMAT,
    TIME_FORMAT,
    areTimesWithinFifteenMinutes,
    getTimeFromMinutes,
    isToday,
} from '../../utils/dates'
import { Dots } from '../Dots'
import { Icon } from '../Icon'
import Button from '../app/design/Button'
import Card from '../app/design/Card'
import Divider from '../app/design/Divider'
import Heading from '../app/design/Heading'
import Label from '../app/design/Label'
import Modal from '../app/design/Modal'
import Paragraph from '../app/design/Paragraph'
import { Checkbox } from '../common/fields/Checkbox'
import { ContactAvatar } from '../contacts/ContactAvatar'
import { CalendarBookableCard } from './CalendarBookableCard'
import { CalendarCurrentTimeLine } from './CalendarCurrentTimeLine'
import { CalendarDayBookables } from './CalendarDayBookables'
import { CalendarDayDraggableBookables } from './CalendarDayDraggableBookables'
import { CalendarDroppable } from './CalendarDroppable'
import { CalendarScrollX } from './CalendarScrollX'

type Props = {
    heightOffset: number
    currentDate: moment.Moment
    pros: User[]
    filter: CalendarFilter
    bookablesByDate: BookablesByDate
    onChangeTime: (
        bookable?: Bookable,
        date?: moment.Moment,
        notify?: boolean,
        changeAll?: boolean
    ) => Promise<void>
}

export const CalendarDay = ({
    heightOffset,
    currentDate,
    pros,
    filter,
    bookablesByDate,
    onChangeTime,
}: Props) => {
    const me = usePSUser()

    const timeRefs = useRef<{ [key: number]: HTMLDivElement }>({})
    const timeLineRef = useRef<null | HTMLDivElement>(null)
    const calendarBodyRef = useRef<HTMLDivElement>(null)
    const allDayTeamRef = useRef<HTMLDivElement>(null)
    const dayTeamRef = useRef<HTMLDivElement>(null)

    const day = currentDate
    const startHour = me && day ? getUserStartHours(me)[day.day()] : 0
    const isTeamAccount = me?.type === 'company'
    const payees = useMemo(() => pros.map((u) => u.subtype) as Payee[], [pros])
    const team = useMemo(
        () =>
            isTeamAccount && filter.hasPeople
                ? pros.filter((pro) => !!pro.id && filter.pros.includes(pro.id))
                : me
                  ? [me]
                  : [],
        [isTeamAccount, pros, filter, me]
    )

    const { activeNodeRect } = useDndContext()
    const [dragState, setDragState] = useState<CalendarDragState>(initialDragState)
    const [isUpdatingTime, setIsUpdatingTime] = useState(false)
    const [changeAll, setChangeAll] = useState(false)
    const [notify, setNotify] = useState(true)

    useScrollIntoView(timeLineRef.current, {
        behavior: 'smooth',
        block: 'center',
        condition: !dragState.state && !isUpdatingTime,
    })

    useScrollIntoView(timeRefs.current[startHour - 1], {
        condition:
            !dragState.state &&
            !isUpdatingTime &&
            !timeLineRef.current &&
            !!startHour &&
            startHour - 1 in timeRefs.current,
    })

    const { width } = useWindowResize()
    const [maxBookablesPerGroup, setMaxBookablesPerGroup] = useState(2)

    useLayoutEffect(() => {
        if (!calendarBodyRef.current) return
        const PADDING_OFFSET = BOOKABLE_PADDING_OFFSET + OVERFLOW_BUTTON_OFFSET
        const MIN_WIDTH = MIN_BOOKABLE_WIDTH + BOOKABLE_GAP
        const availableWidth =
            (calendarBodyRef.current.clientWidth - TIME_COLUMN_OFFSET) / team.length -
            PADDING_OFFSET
        const maxBookables = Math.floor(availableWidth / MIN_WIDTH)
        setMaxBookablesPerGroup(maxBookables > 0 ? maxBookables : 1)
    }, [width, team])

    const allDayBookables = useMemo(
        () =>
            (bookablesByDate[day.format(DATE_KEY_FORMAT)] ?? []).filter((b: Bookable) =>
                isMultiDay(b)
            ),
        [bookablesByDate, day]
    )

    const bookables = useMemo(
        () =>
            groupOverlappingSortedBookables(
                day,
                (bookablesByDate[day.format(DATE_KEY_FORMAT)] ?? [])
                    .filter((b: Bookable) => !isMultiDay(b))
                    .sort(sortBookablesByStartTime)
            ),
        [bookablesByDate, day]
    )

    const isEmployeeAssigned = (employeeId: string | undefined, bookable: Bookable) => {
        if (!employeeId) return false
        if ([bookable.person?.id, bookable.employee?.id, bookable.owner?.id].includes(employeeId))
            return true
        if (bookable instanceof Booking)
            return bookable.jobs.map((j) => j.person?.id).includes(employeeId)
        return false
    }

    const getAllDayBookablesByEmployee = useCallback(
        (employeeId: string | undefined): Bookable[] =>
            (bookablesByDate[day.format(DATE_KEY_FORMAT)] ?? [])
                .filter((bookable: Bookable) => isEmployeeAssigned(employeeId, bookable))
                .filter((b: Bookable) => isMultiDay(b)),
        [bookablesByDate, day]
    )

    const hasAllDayBookables = useMemo(
        () =>
            isTeamAccount
                ? team.some((u) => !!u.id && getAllDayBookablesByEmployee(u.id).length > 0)
                : allDayBookables.length > 0,
        [isTeamAccount, team, getAllDayBookablesByEmployee, allDayBookables]
    )

    const getBookablesByEmployee = useCallback(
        (employeeId: string | undefined) =>
            groupOverlappingSortedBookables(
                day,
                (bookablesByDate[day.format(DATE_KEY_FORMAT)] ?? [])
                    .filter((bookable: Bookable) => isEmployeeAssigned(employeeId, bookable))
                    .filter((b: Bookable) => !isMultiDay(b))
                    .sort(sortBookablesByStartTime)
            ),
        [bookablesByDate, day]
    )

    const handleDragStart = useCallback(
        ({ active }: CalendarDragStartEvent) => {
            let offset = 0
            offset += calendarBodyRef.current?.scrollTop ? calendarBodyRef.current?.scrollTop : 0
            offset -= heightOffset + DAY_HEADER_HEIGHT + CALENDAR_PADDING
            offset -= hasAllDayBookables ? ALL_DAY_HEADER_HEIGHT : 0

            setDragState((prev) => ({
                ...prev,
                offset,
                state: 'start',
                bookable: active.data.current.bookable,
            }))
        },
        [heightOffset, hasAllDayBookables]
    )

    const handleDragMove = ({ delta }: DragMoveEvent) => {
        if (!activeNodeRect) return

        const start = activeNodeRect.top + dragState.offset + delta.y
        const minutesFromPixels = start / PIXELS_PER_MINUTE
        const roundedMinutes = Math.round(minutesFromPixels / 5) * 5
        const newTime = moment(getTimeFromMinutes(roundedMinutes), TIME_FORMAT)
        const date = day.clone().set({
            hour: newTime.hour(),
            minute: newTime.minute(),
        })

        const HOUR_PADDING_OFFSET = 8
        const snappedTop = roundedMinutes * PIXELS_PER_MINUTE - HOUR_PADDING_OFFSET

        setDragState((prev) => ({
            ...prev,
            state: 'move',
            top: Math.max(snappedTop, 0),
            date,
        }))
    }

    const handleDragEnd = () => {
        const shouldCancel =
            !dragState.date ||
            !dragState.bookable?.date ||
            dragState.date.isSame(dragState.bookable?.date)

        setDragState((prev) =>
            shouldCancel ? { ...initialDragState, state: 'cancel' } : { ...prev, state: 'end' }
        )
    }

    useDndMonitor({
        onDragStart: handleDragStart,
        onDragMove: handleDragMove,
        onDragEnd: handleDragEnd,
    })

    const hideHourLabel = useCallback(
        (hour: string) => {
            return (
                dragState.top >= 0 &&
                !!dragState.date &&
                areTimesWithinFifteenMinutes(dragState.date.format(TIME_FORMAT), hour)
            )
        },
        [dragState.top, dragState.date]
    )

    return (
        <Card
            className="mt-6 mb-8"
            style={{
                height: `calc(100% - ${heightOffset}px)`,
                maxWidth: '100%',
                minWidth: '1024px',
            }}
        >
            <div
                className="flex items-center rounded-t-lg border-[1px] border-gray-300 bg-gray-100"
                style={{ height: `${DAY_HEADER_HEIGHT}px` }}
            >
                <div className="flex h-full min-w-[98px] flex-col items-center justify-center border-r-[1px] border-r-gray-300">
                    <Paragraph size="xs" className="text-gray-600">
                        {moment().format('z')}
                    </Paragraph>
                </div>
                {(!isTeamAccount || !filter.hasPeople) && (
                    <div className="mx-auto my-0 flex h-full min-w-[114px] flex-1 flex-col items-center justify-center">
                        <Paragraph size="sm" className="text-gray-600">
                            {day.format(SHORT_DOW_FORMAT)}
                        </Paragraph>
                        <Heading size="xs" className="text-gray-700">
                            {day.date()}
                        </Heading>
                    </div>
                )}
                {isTeamAccount && filter.hasPeople && (
                    <CalendarScrollX
                        deps={[team]}
                        scrollAmount={DAY_MIN_WIDTH}
                        scrollRefs={[allDayTeamRef, dayTeamRef]}
                        style={{
                            wrapper: { height: '100%' },
                            leftControl: { left: '-18px' },
                            rightControl: { right: '-18px' },
                        }}
                    >
                        {team.map((employee) => (
                            <div
                                key={employee.id}
                                className="flex min-w-[114px] flex-1 flex-col items-center justify-center gap-2 border-r-[1px] border-r-gray-300 last:border-r-0"
                            >
                                <ContactAvatar
                                    user={employee}
                                    size="sm"
                                    className="border-2 border-white"
                                />
                                <Label
                                    size="sm"
                                    className="w-full truncate px-2 text-center text-gray-700"
                                >
                                    {employee.name}
                                </Label>
                            </div>
                        ))}
                    </CalendarScrollX>
                )}
            </div>
            {hasAllDayBookables && (
                <div
                    className="flex border-[1px] border-t-0 border-gray-300 bg-gray-100"
                    style={{ height: `${ALL_DAY_HEADER_HEIGHT}px` }}
                >
                    <div className="flex h-full min-w-[98px] flex-col items-center justify-center border-r-[1px] border-r-gray-300">
                        <Label size="xs" className="text-gray-700">
                            All day
                        </Label>
                    </div>
                    {!isTeamAccount && (
                        <div className="flex min-w-[114px] flex-1 flex-col p-1">
                            <CalendarDayBookables
                                day={day}
                                payees={payees}
                                bookables={allDayBookables}
                                threshold={allDayBookables.length === 3 ? 3 : 2}
                            />
                        </div>
                    )}
                    {isTeamAccount && (
                        <div className="flex w-full overflow-hidden" ref={allDayTeamRef}>
                            {team.map((employee) => (
                                <div
                                    key={employee.id}
                                    className="flex min-w-[114px] flex-1 flex-col border-r-[1px] border-r-gray-300 p-1 last:border-r-0"
                                >
                                    <CalendarDayBookables
                                        day={day}
                                        payees={payees}
                                        bookables={getAllDayBookablesByEmployee(employee.id)}
                                        threshold={
                                            getAllDayBookablesByEmployee(employee.id).length === 3
                                                ? 3
                                                : 2
                                        }
                                    />
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
            <div
                ref={calendarBodyRef}
                className="scrollbar-hide relative flex overflow-x-clip overflow-y-auto overscroll-contain rounded-b-lg border-[1px] border-t-0 border-gray-300"
                style={{
                    height: `calc(100% - ${hasAllDayBookables ? DAY_HEADER_HEIGHT + ALL_DAY_HEADER_HEIGHT : DAY_HEADER_HEIGHT}px)`,
                }}
            >
                <div className="flex h-fit min-w-[98px] flex-col items-center justify-start gap-[48px] border-r-[1px] border-r-gray-300 px-0 py-[57px]">
                    {DAILY_HOURS.slice(1).map((hour, index) => (
                        <div
                            key={hour}
                            style={{
                                transition: 'opacity 0.2s ease-in-out',
                                opacity: hideHourLabel(hour) ? 0 : 1,
                            }}
                            ref={(el) => {
                                if (el) timeRefs.current[index + 1] = el
                            }}
                            className="w-full"
                        >
                            <div className="flex items-center justify-between">
                                <Paragraph size="xs" className="mx-auto text-gray-600">
                                    {hour}
                                </Paragraph>
                                <div className="h-px w-4 border-b-[1px] border-b-gray-300 pt-px"></div>
                            </div>
                        </div>
                    ))}
                </div>
                {dragState.top >= 0 && !!dragState.date && (
                    <div
                        className="text-info-400 absolute left-6 flex items-center justify-center gap-2"
                        style={{ top: dragState.top }}
                    >
                        <Label size="xs">{dragState.date.format(TIME_FORMAT).slice(0, -2)}</Label>
                        {isUpdatingTime && <Dots />}
                    </div>
                )}
                <div className="flex h-fit w-full overflow-hidden" ref={dayTeamRef}>
                    {team.map((employee) => (
                        <div
                            key={employee.id}
                            className="relative flex h-fit min-w-[114px] flex-1 flex-col items-center justify-center gap-[67px] overflow-hidden border-r-[1px] border-r-gray-300 px-0 py-[67px] first:overflow-visible last:border-r-0"
                        >
                            {!!employee.id && (
                                <CalendarDroppable
                                    className="absolute top-0 left-0 size-full p-1"
                                    id={employee.id}
                                >
                                    {!isTeamAccount &&
                                        bookables.map((bookablesGroup) => (
                                            <CalendarDayDraggableBookables
                                                key={bookablesGroup.start}
                                                day={day}
                                                payees={payees}
                                                bookablesGroup={bookablesGroup}
                                                threshold={maxBookablesPerGroup}
                                                disableDrag={isUpdatingTime}
                                            />
                                        ))}

                                    {isTeamAccount &&
                                        getBookablesByEmployee(employee.id).map(
                                            (bookablesGroup) => (
                                                <CalendarDayDraggableBookables
                                                    key={bookablesGroup.start}
                                                    day={day}
                                                    payees={payees}
                                                    bookablesGroup={bookablesGroup}
                                                    threshold={maxBookablesPerGroup}
                                                    disableDrag={isUpdatingTime}
                                                />
                                            )
                                        )}
                                </CalendarDroppable>
                            )}
                            {DAILY_HOURS.slice(1).map((hour) => (
                                <div
                                    key={hour}
                                    className="self-stretch border-t-[1px] border-t-gray-300"
                                ></div>
                            ))}
                        </div>
                    ))}
                    <DragOverlay dropAnimation={null} modifiers={[restrictToVerticalAxis]}>
                        {!!dragState.bookable && (
                            <CalendarBookableCard
                                bookable={dragState.bookable}
                                day={day}
                                payees={payees}
                                size={getBookableCardSize(day, dragState.bookable)}
                                style={{
                                    height:
                                        getBookableDuration(dragState.bookable) * PIXELS_PER_MINUTE,
                                    boxShadow: '0px 5px 20px rgba(0, 0, 0, 0.2)',
                                }}
                            />
                        )}
                    </DragOverlay>
                </div>
                {isToday(day) && <CalendarCurrentTimeLine ref={timeLineRef} />}
            </div>
            <Modal
                size={
                    dragState.bookable?.isRecurring || dragState.bookable?.allowNotify
                        ? 'large'
                        : 'small'
                }
                showDividers={dragState.bookable?.isRecurring || dragState.bookable?.allowNotify}
                isOpen={dragState.state === 'end'}
                title="Change time?"
                onClose={() =>
                    setDragState((prev) =>
                        isUpdatingTime
                            ? { ...prev, state: '' }
                            : { ...initialDragState, state: 'cancel' }
                    )
                }
                footer={
                    <Button
                        intent="primary"
                        isLoading={isUpdatingTime}
                        onClick={() => {
                            setIsUpdatingTime(true)
                            onChangeTime(dragState.bookable, dragState.date, notify, changeAll)
                        }}
                    >
                        Yes, change
                    </Button>
                }
            >
                <div className="flex flex-col gap-8">
                    <Paragraph className="text-gray-600">
                        Your {dragState.bookable ? getBookableType(dragState.bookable) : 'bookable'}{' '}
                        will be moved to <Label>{dragState.date?.format(TIME_FORMAT)}</Label>
                    </Paragraph>

                    {dragState.bookable?.isRecurring && (
                        <>
                            <div
                                className="focus-visible:border-info-400 -m-4 flex cursor-pointer items-center gap-4 rounded-lg p-4 text-gray-600 hover:bg-linear-to-b hover:from-black/4 hover:to-black/4 hover:text-gray-600 focus:text-gray-600 focus-visible:border-2 focus-visible:outline-hidden active:bg-white active:bg-linear-to-b active:from-black/8 active:to-black/8"
                                tabIndex={0}
                                onClick={() => setChangeAll(false)}
                            >
                                <div className="flex size-12 items-center justify-center rounded-full bg-gray-100">
                                    <Icon icon="calendar.large" background="round" />
                                </div>

                                <Label className="flex-1 text-gray-700">
                                    This{' '}
                                    {dragState.bookable
                                        ? getBookableType(dragState.bookable)
                                        : 'bookable'}
                                </Label>

                                <Checkbox
                                    style="radio"
                                    isChecked={!changeAll}
                                    onChange={() => setChangeAll(false)}
                                />
                            </div>

                            <div
                                className="focus-visible:border-info-400 -m-4 flex cursor-pointer items-center gap-4 rounded-lg p-4 text-gray-600 hover:bg-linear-to-b hover:from-black/4 hover:to-black/4 hover:text-gray-600 focus:text-gray-600 focus-visible:border-2 focus-visible:outline-hidden active:bg-white active:bg-linear-to-b active:from-black/8 active:to-black/8"
                                tabIndex={0}
                                onClick={() => setChangeAll(true)}
                            >
                                <div className="text-primary-700 flex size-12 items-center justify-center">
                                    <Icon icon="repeat.large" background="round" />
                                </div>

                                <Label className="flex-1 text-gray-700">
                                    This and following{' '}
                                    {dragState.bookable
                                        ? getBookableType(dragState.bookable, true)
                                        : 'bookable'}
                                </Label>

                                <Checkbox
                                    style="radio"
                                    isChecked={changeAll}
                                    onChange={() => setChangeAll(true)}
                                />
                            </div>
                        </>
                    )}
                    {dragState.bookable?.isRecurring && dragState.bookable.allowNotify && (
                        <Divider className="my-0" />
                    )}
                    {dragState.bookable?.allowNotify && (
                        // TODO: consider reusable clickable Field UI component (e.g. see NavField, LinkCard > Clickable, etc.)
                        <div
                            className="focus-visible:border-info-400 -m-4 flex cursor-pointer items-center gap-4 rounded-lg p-4 text-gray-600 hover:bg-linear-to-b hover:from-black/4 hover:to-black/4 hover:text-gray-600 focus:text-gray-600 focus-visible:border-2 focus-visible:outline-hidden active:bg-white active:bg-linear-to-b active:from-black/8 active:to-black/8"
                            tabIndex={0}
                            onClick={() => setNotify(!notify)}
                        >
                            <div className="text-primary-700 flex size-12 items-center justify-center">
                                <Icon icon="bell.large" background="round" />
                            </div>

                            <Label className="pointer-events-none flex-1 text-gray-700">
                                Notify client
                            </Label>

                            <Checkbox isChecked={notify} onChange={() => setNotify(!notify)} />
                        </div>
                    )}
                </div>
            </Modal>
        </Card>
    )
}
